import { redirect } from '@tanstack/react-router';
import { COMMAND_CONFLICT_MESSAGES } from 'cpool-constants';
import { schemaId } from 'cpool-schemas';
import { sleep, UnauthorizedError } from 'cpool-utils';
import { v4 as uuid } from 'uuid';
import { z } from 'zod';

import { API } from '../config';
import { login, logout, setUser, useUserStore } from '../store/userStore';
import type { ApiRawReturnType } from '~/features/auth/types/apiRawReturnType';
import type { ApiValidateReturnType } from '~/features/auth/types/apiValidateReturnType';
import { hardNavigate } from '~/utils/hardNavigate';

// TODO: In eigene Datei auslagern
export class APIError extends Error {
    public readonly info: string;

    public readonly status: number;

    public readonly url: string;

    constructor({ info, status, url }: { info: string; status: number; url: string }) {
        super(`API Error: "${info}" (${status})`);
        this.info = info;
        this.status = status;
        this.url = url;
    }
}

export class APICommunicator {
    static renewPromise: Promise<Response>;

    static renewLock = false;

    /**
     * logs the user out (changes state with/without calling backend)
     * @param {boolean} sendBackendRequest
     * @returns success true and respose object or success false and error message
     */
    static async logout(sendBackendRequest = true): Promise<boolean> {
        let success = true;
        if (sendBackendRequest) {
            // plain fetch: apiFetch would renew or re-enter logout on 401/403
            try {
                const response = await fetch(`${API.URL}/logout`, { credentials: 'include', method: 'GET' });
                success = response.ok;
            } catch {
                success = false;
            }
        }
        logout();
        return success;
    }

    /**
     * logs the user in
     * @param {string} email
     * @param {string} password
     * @param {string} code
     * @returns success true and data null or success false and error message
     */
    static async login(email: string, password: string, code: string): Promise<ApiRawReturnType> {
        const response = await fetch(`${API.URL}/login`, {
            body: JSON.stringify({ code, email, password }),
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            method: 'POST',
        });
        if (response.ok) {
            const json = await response.json();
            const { rtDecoded, user } = json;

            if (!rtDecoded || !user) {
                throw new Error('Missing user or rtDecoded in response');
            }

            login(user, rtDecoded);
            return { success: true };
        }
        return { error: 'Authentifizierung fehlgeschlagen', success: false };
    }

    /**
     * checks validity of email/password combination
     * @param {string} email
     * @param {string} password
     * @returns
     */
    static async checkUser(email: string, password: string): Promise<ApiRawReturnType> {
        const response = await fetch(`${API.URL}/checkUser`, {
            body: JSON.stringify({ email, password }),
            credentials: 'omit',
            headers: {
                'Content-Type': 'application/json',
            },
            method: 'POST',
        });
        if (response.ok) return { success: true } as const;

        return { error: 'Login fehlgeschlagen', success: false } as const;
    }

    /**
     * renews the JWT using the Refresh Token
     * @returns boolean - success or failure
     */
    static async renew() {
        if (this.renewPromise && this.renewLock) return this.renewPromise;
        const { rtDecoded } = useUserStore.getState();
        if (!rtDecoded) return false;

        this.renewLock = true;

        this.renewPromise = fetch(`${API.URL}/login/renew`, {
            credentials: 'include',
            method: 'GET',
        });
        const response = await this.renewPromise;
        this.renewLock = false;

        if (!response.ok) {
            logout();
            return false;
        }
        const json = await response.json();
        const { user } = json;
        if (!user) {
            throw new Error('Missing user in response');
        }
        setUser(user);
        return true;
    }

    /**
     * allows an administrator to login as a regular user
     * @param {string} userId
     * @returns success - true or false
     */
    static async impersonate(userId: string): Promise<ApiRawReturnType> {
        if (this.renewPromise && this.renewLock) await this.renewPromise;

        const response = await fetch(`${API.URL}/login/impersonate`, {
            body: JSON.stringify({ userId }),
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            method: 'POST',
        });

        if (response.ok) {
            const json = await response.json();
            const { rtDecoded, user } = json;
            if (!rtDecoded || !user) {
                throw new Error('Missing user or rtDecoded in response');
            }

            login(user, rtDecoded); // do we need to do something with the data from the store?
            return { success: true };
        }
        return {
            error: `Immitierung von User ${userId} fehlgeschlagen`,
            success: false,
        };
    }

    /**
     * revokes administtrator impersonation of a regular user
     * @returns success - true or false
     */
    static async revokeImpersonation(): Promise<ApiRawReturnType> {
        if (this.renewPromise && this.renewLock) await this.renewPromise;
        const { rtDecoded: rtDecodedState } = useUserStore.getState();
        if (!rtDecodedState)
            return {
                error: 'No refresh token available',
                success: false,
            };

        const response = await fetch(`${API.URL}/login/impersonate/revoke`, {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            method: 'POST',
        });
        if (response.ok) {
            // TODO - we leave this in for now until we removed every hook that uses the "other" user slice
            const json = await response.json();
            const { rtDecoded, user } = json;
            if (!user || !rtDecoded) {
                throw new Error('Missing user or rtDecoded in response');
            }
            login(user, rtDecoded);
            return { success: true };
        }
        return {
            error: 'Wiederrufen der Immitierung fehlgeschlagen',
            success: false,
        };
    }

    /**
     * General method to call Backend-API
     * @param {string} route
     * @param {string} context - request configuration
     * @returns success true and data object or success false and error message
     * @throws Error wenns nen Fehler gibt TODO
     * TODO: Nachrichten schöner machen
     * TODO: Schauen ob wir den content-type
     */
    static async apiFetch(
        {
            context = {},
            route,
            shouldThrow = true,
        }: {
            route: string;
            context?: RequestInit;
            shouldThrow?: boolean;
        },
        repeatTimes = 0,
    ): Promise<Response> {
        const url = `${API.URL}${route}`;
        let { user } = useUserStore.getState();

        if (!user) {
            const isUserRenewed = await this.renew();
            if (!isUserRenewed) {
                this.logout(false);
                throw new UnauthorizedError('User renew failed');
            }
            user = useUserStore.getState().user;

            // we assume that we now have a JWT and can continue
        }

        const { headers, ...restContext } = context;

        const newHeaders = new Headers({
            Accept: '*/*',
            'Content-Type': 'application/json',
            ...headers,
        });

        if (headers && 'Content-Type' in headers && headers['Content-Type'] === 'multipart/form-data') {
            newHeaders.delete('Content-Type');
        }

        const response = await fetch(url, {
            credentials: 'include',
            headers: newHeaders,
            ...restContext,
        });

        if (!response.ok && shouldThrow === true) {
            const bodyText = await response.text();
            if (bodyText === 'User is blocked') {
                await this.logout();
                // the cleared store would otherwise send the app shell to /login
                hardNavigate('/accessdenied');
                throw redirect({ to: '/accessdenied' });
            }
            // backend answers 403 for an expired JWT too -> renew once, repeat once, then redirect
            if (response.status === 401 || response.status === 403) {
                if (repeatTimes >= 1) {
                    throw redirect({ to: '/accessdenied' });
                }
                const isJWTRenewed = await this.renew();
                if (!isJWTRenewed) {
                    this.logout(false);
                    throw redirect({ to: '/accessdenied' });
                }

                return this.apiFetch(
                    {
                        context,
                        route,
                        shouldThrow,
                    },
                    repeatTimes + 1,
                );
            }
            // Optimistic locking conflict -> retry up to 3 times
            if (bodyText === 'Data not yet available') {
                if (repeatTimes >= 3) {
                    throw new APIError({ info: bodyText || 'Request failed', status: response.status, url: route });
                }
                // Exponential backoff (max: 375ms up to 3000ms)
                // eslint-disable-next-line no-bitwise
                const milliseconds = (Math.random() + 0.5) * (1 << repeatTimes) * 250;
                await sleep(milliseconds);
                const resultRecoursive = await this.apiFetch(
                    {
                        context,
                        route,
                        shouldThrow,
                    },
                    repeatTimes + 1,
                );
                return resultRecoursive;
            }
            throw new APIError({ info: bodyText || 'Request failed', status: response.status, url: route });
        }

        return response;
    }

    /**
     * sends a fetch request to any external API
     * @param {string} baseUrl
     * @param {string} route
     * @param {string} context
     * @param {string} jwt - optional
     * @returns
     */
    static async externalFetch({
        baseUrl,
        context,
        jwt,
        route,
    }: {
        baseUrl: string;
        route: string;
        context: RequestInit;
        jwt?: string;
    }) {
        if (jwt) {
            context.headers = {
                ...context.headers,
                // Authorization: `Bearer ${jwt}`,
            };
        }

        return fetch(`${baseUrl}${route}`, {
            credentials: 'include',
            ...context,
        });
    }

    /**
     * allows sending different commands.
     * Blackrik no-ops return HTTP 200 + JSON `null` (no event published). Pass `{ allowNoop: true }`
     * to receive `null` instead of letting `schemaPosition.parse` throw ZodError.
     */
    static async sendCommand(
        cmd: {
            aggregateName: string;
            commandName: string;
            aggregateId?: string;
            payload: Record<string, unknown>;
        },
        opts: { allowNoop: true },
    ): Promise<{ position: number; aggregateId: string; id: string } | null>;
    static async sendCommand(cmd: {
        aggregateName: string;
        commandName: string;
        aggregateId?: string;
        payload: Record<string, unknown>;
    }): Promise<{ position: number; aggregateId: string; id: string }>;
    static async sendCommand(
        {
            aggregateId: receivedAggregateId,
            aggregateName,
            commandName,
            payload,
        }: {
            aggregateName: string;
            commandName: string;
            aggregateId?: string;
            payload: Record<string, unknown>;
        },
        opts?: { allowNoop: true },
    ): Promise<{ position: number; aggregateId: string; id: string } | null> {
        // Hoisted once — reused across 409 retries (must not regenerate per attempt).
        const aggregateId = receivedAggregateId ?? uuid();
        const schemaPosition = z.object({
            aggregateId: z.string(),
            id: schemaId,
            position: z.number(),
        });

        // Retry loop (not recursion): keeps `aggregateId`/`opts` outside the loop so 409 retries
        // cannot drop allowNoop or regenerate the id. Awaits are intentional sequential backoff.
        /* eslint-disable no-await-in-loop -- sequential 409 retry with backoff */
        for (let attempt = 0; ; attempt++) {
            const response = await this.apiFetch({
                context: {
                    body: JSON.stringify({
                        aggregateId,
                        payload,
                    }),
                    method: 'POST',
                },
                route: `/command/${aggregateName}/${commandName}`,
                shouldThrow: false,
            });

            if (response.ok) {
                const parsedBody: unknown = await response.json();
                // Non-allowNoop: leave as schemaPosition.parse — JSON null → ZodError (unchanged for
                // the other ~56 callers). Do not replace with an explicit throw (would change error shape).
                if (parsedBody === null && opts?.allowNoop) {
                    return null; // documented no-op contract — no event published
                }
                return schemaPosition.parse(parsedBody);
            }

            if (response.status >= 400) {
                const responseText = await response.text();
                // Only the event store's optimistic locking conflict is retried (up to 3 times); domain 409s are final
                if (
                    response.status === 409 &&
                    responseText === COMMAND_CONFLICT_MESSAGES.EVENTS_OVERLAPPED &&
                    attempt < 3
                ) {
                    // Exponential backoff (max: 375ms up to 3000ms)
                    // eslint-disable-next-line no-bitwise
                    const milliseconds = (Math.random() + 0.5) * (1 << attempt) * 250;
                    await sleep(milliseconds);
                } else {
                    const error = new Error('Command failed.');
                    // @ts-expect-error - we set the property here, so it does exist then ...
                    error.status = response.status;
                    // @ts-expect-error - we set the property here, so it does exist then ...
                    error.response = responseText;
                    throw error;
                }
            } else {
                throw new Error('Command failed!');
            }
        }
        /* eslint-enable no-await-in-loop */
    }

    /**
     * sends reset password or reset 2FA Code requests
     * @param {string} path
     * @param {object} config - request configuration
     * @returns either success true and data or success false and error message
     */
    static async resetResource<TData>({
        config,
        path,
        schema,
    }: {
        path: string;
        schema: z.Schema<TData>;
        config: RequestInit;
    }): Promise<ApiValidateReturnType<TData>> {
        try {
            const jwt = new URLSearchParams(window.location.search).get('jwt');
            const url = jwt ? `${API.URL}${path}?jwt=${jwt}` : `${API.URL}${path}`;
            const response = await fetch(url, {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                ...config,
            });
            if (response.ok) {
                const data = await response.json();
                const validatedData = schema.parse(data);
                return {
                    data: validatedData as TData,
                    success: true,
                };
            }
            return {
                error: response.statusText,
                success: false,
            };
        } catch (error) {
            return {
                error: error instanceof Error ? error.message : 'Request failed!',
                success: false,
            };
        }
    }
}
